import { useEffect, useState } from "react";
import { toast } from "sonner";
import moment from "moment";
import { X, Plus } from "lucide-react";
import { getDoctor, getDoctorAppointments, clearDoctorDelay, addDoctorLeave, removeDoctorLeave } from "../api/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Avatar, AvatarFallback } from "./ui/avatar";

export default function DoctorDrawer({ doctorId, onClose, onMarkDelay, refreshToken }) {
  const [doctor, setDoctor] = useState(null);
  const [history, setHistory] = useState([]);
  const [addingLeave, setAddingLeave] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ start: "", end: "", reason: "", notify: true });

  const load = () => {
    if (!doctorId) return;
    getDoctor(doctorId)
      .then((res) => setDoctor(res.data))
      .catch((err) => toast.error(err?.message || "Could not load doctor."));
    getDoctorAppointments(doctorId, { limit: 10 })
      .then((res) => setHistory(res.data))
      .catch((err) => toast.error(err?.message || "Could not load appointment history."));
  };

  useEffect(load, [doctorId, refreshToken]);

  if (!doctorId) return null;

  const initials = (doctor?.name || "")
    .replace("Dr ", "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  const handleClearDelay = async () => {
    await clearDoctorDelay(doctorId);
    toast.success("Delay cleared.");
    load();
  };

  const handleAddLeave = async () => {
    if (!leaveForm.start) {
      toast.error("Please choose a start date.");
      return;
    }
    const res = await addDoctorLeave(doctorId, leaveForm);
    toast.success(
      res.data.notified > 0
        ? `Leave added — ${res.data.notified} appointment(s) flagged for reschedule.`
        : "Leave added."
    );
    setAddingLeave(false);
    setLeaveForm({ start: "", end: "", reason: "", notify: true });
    load();
  };

  const handleRemoveLeave = async (leaveId) => {
    await removeDoctorLeave(doctorId, leaveId);
    toast.success("Leave removed.");
    load();
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-card p-6 shadow-xl">
        <button onClick={onClose} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
        </button>

        {doctor && (
          <>
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-serif text-lg font-semibold">{doctor.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {doctor.specialty} · {doctor.reg_no} · ₹{doctor.fee}
                </p>
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              {doctor.on_leave_today && <Badge variant="destructive">On leave today</Badge>}
              {doctor.delay && <Badge variant="warning">Delayed +{doctor.delay.minutes}m</Badge>}
              {!doctor.on_leave_today && !doctor.delay && <Badge variant="secondary">On duty</Badge>}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <h4 className="mb-2 text-sm font-semibold">Today's delay</h4>
              {doctor.delay ? (
                <div className="rounded-md bg-warning/10 p-3 text-sm">
                  <p>
                    +{doctor.delay.minutes} min — {doctor.delay.reason}
                  </p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={handleClearDelay}>
                    Clear
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => onMarkDelay(doctor)}>
                  Mark delay for today
                </Button>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold">Upcoming leave</h4>
                <button className="flex items-center gap-1 text-sm text-primary" onClick={() => setAddingLeave((v) => !v)}>
                  <Plus className="h-3.5 w-3.5" /> Add leave
                </button>
              </div>

              {addingLeave && (
                <div className="mb-3 space-y-2 rounded-md border border-border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={leaveForm.start}
                      onChange={(e) => setLeaveForm((f) => ({ ...f, start: e.target.value }))}
                    />
                    <Input
                      type="date"
                      value={leaveForm.end}
                      onChange={(e) => setLeaveForm((f) => ({ ...f, end: e.target.value }))}
                    />
                  </div>
                  <Input
                    placeholder="Reason"
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={leaveForm.notify}
                      onCheckedChange={(v) => setLeaveForm((f) => ({ ...f, notify: v }))}
                    />
                    Flag & notify patients already booked in this range
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAddingLeave(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleAddLeave}>
                      Save
                    </Button>
                  </div>
                </div>
              )}

              {doctor.upcoming_leaves.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming leave.</p>
              ) : (
                <ul className="space-y-2">
                  {doctor.upcoming_leaves.map((leave) => (
                    <li key={leave._id} className="flex items-center justify-between rounded-md border border-border p-2 text-sm">
                      <span>
                        {moment(leave.start).format("D MMM")} – {moment(leave.end).format("D MMM")} · {leave.reason}
                      </span>
                      <button onClick={() => handleRemoveLeave(leave._id)} className="text-muted-foreground hover:text-destructive">
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <h4 className="mb-2 text-sm font-semibold">Appointment history</h4>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No appointments yet.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((a) => (
                    <li key={a._id} className="flex items-center justify-between text-sm">
                      <span>
                        {a.iso_date} · {a.patient_id?.name} · {a.slot_time}
                      </span>
                      <Badge
                        variant={a.status === "completed" ? "secondary" : a.status === "cancelled" ? "destructive" : "default"}
                      >
                        {a.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
